import { useStore } from 'zustand';
import { useRouter } from 'next/navigation';

import { authStore } from '@/store/auth.store';
import {
  addBranchFavorite,
  addBranchLaboratoryFavorite,
  addPartnerFavorite,
  addProfessionalFavorite,
  checkIfBranchIsFavorite,
  checkIfBranchLaboratoryIsFavorite,
  checkIfPartnerIsFavorite,
  checkIfProfessionalIsFavorite,
  removeBranchFavorite,
  removeBranchLaboratoryFavorite,
  removePartnerFavorite,
  removeProfessionalFavorite,
} from '@/app/(main)/(pages)/(auth)/favoritos/_services/favorites.service';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { useToast } from '@/components/ui/use-toast';

export interface UseFavoriteProps {
  type: EntityTypeEnum;
  id: string;
}

export const keyName = {
  [EntityTypeEnum.PROFESSIONAL]: 'profissional',
  [EntityTypeEnum.CLINIC]: 'local',
  [EntityTypeEnum.LABORATORY_COLLECT_POINT]: 'laboratório',
  [EntityTypeEnum.BRANCH]: 'local',
};

const keyAddFunction = {
  [EntityTypeEnum.PROFESSIONAL]: (id: string) => addProfessionalFavorite(id),
  [EntityTypeEnum.CLINIC]: (id: string) => addPartnerFavorite(id),
  [EntityTypeEnum.LABORATORY_COLLECT_POINT]: (id: string) => addBranchLaboratoryFavorite(id),
  [EntityTypeEnum.BRANCH]: (id: string) => addBranchFavorite(id),
};

const keyRemoveFunction = {
  [EntityTypeEnum.PROFESSIONAL]: (id: string) => removeProfessionalFavorite(id),
  [EntityTypeEnum.CLINIC]: (id: string) => removePartnerFavorite(id),
  [EntityTypeEnum.LABORATORY_COLLECT_POINT]: (id: string) => removeBranchLaboratoryFavorite(id),
  [EntityTypeEnum.BRANCH]: (id: string) => removeBranchFavorite(id),
};

const keyCheckFavoriteStatusFunction = {
  [EntityTypeEnum.PROFESSIONAL]: (id: string) => checkIfProfessionalIsFavorite(id),
  [EntityTypeEnum.CLINIC]: (id: string) => checkIfPartnerIsFavorite(id),
  [EntityTypeEnum.LABORATORY_COLLECT_POINT]: (id: string) => checkIfBranchLaboratoryIsFavorite(id),
  [EntityTypeEnum.BRANCH]: (id: string) => checkIfBranchIsFavorite(id),
};

export const useFavorite = () => {
  const { toast } = useToast();
  const routes = useRouter();
  const isLoggedIn = useStore(authStore, (state) => state.computed.isLoggedIn);

  async function addFavorite({ type, id }: UseFavoriteProps) {
    if (isLoggedIn) {
      try {
        await keyAddFunction[type](id);

        toast.success(`O ${keyName[type]} foi adicionado à sua`, {
          action: {
            text: 'Lista de Favoritos',
            onClick: () => routes.push('/favoritos'),
          },
        });
        return true;
      } catch {
        toast.error(`Erro ao adicionar ${keyName[type]} a sua`, {
          action: {
            text: 'Lista de Favoritos',
            onClick: () => routes.push('/favoritos'),
          },
        });
        return false;
      }
    }
  }

  async function removeFavorite({ type, id }: UseFavoriteProps) {
    if (isLoggedIn) {
      try {
        await keyRemoveFunction[type](id);

        toast.success(`O ${keyName[type]} foi removido da sua`, {
          action: {
            text: 'Lista de Favoritos',
            onClick: () => routes.push('/favoritos'),
          },
        });
        return true;
      } catch {
        toast.error(`Erro ao remover ${keyName[type]} a sua`, {
          action: {
            text: 'Lista de Favoritos',
            onClick: () => routes.push('/favoritos'),
          },
        });
        return false;
      }
    }
  }

  async function checkFavoriteStatus({ type, id }: UseFavoriteProps) {
    if (isLoggedIn) {
      try {
        return await keyCheckFavoriteStatusFunction[type](id);
      } catch {
        toast.error(`Erro ao verificar se ${keyName[type]} está na sua`, {
          action: {
            text: 'Lista de Favoritos',
            onClick: () => routes.push('/favoritos'),
          },
        });
        return false;
      }
    }
  }

  return { addFavorite, removeFavorite, checkFavoriteStatus };
};
